import { useEffect, useRef, type KeyboardEvent } from 'react';

interface Props {
  id?: string;
  label: string;
  value: number;
  min?: number;
  max: number;
  onCommit: (value: number) => void;
}

export function DimensionInput({ id, label, value, min = 16, max, onCommit }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) inputRef.current.value = String(value);
  }, [value]);

  function commit() {
    if (!inputRef.current) return;
    const parsed = Number.parseInt(inputRef.current.value, 10);
    const next = Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : value;
    inputRef.current.value = String(next);
    if (next !== value) onCommit(next);
  }

  function keyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') event.currentTarget.blur();
    if (event.key === 'Escape') {
      event.currentTarget.value = String(value);
      event.currentTarget.blur();
    }
  }

  return (
    <label htmlFor={id}>
      {label}
      <input
        ref={inputRef}
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        defaultValue={value}
        onBlur={commit}
        onKeyDown={keyDown}
        onWheel={(event) => event.currentTarget.blur()}
      />
    </label>
  );
}
